import {useCallback, useEffect, useRef, useState} from "react";
import {Loader, Text} from "@mantine/core";
import {useTranslation} from "react-i18next";
import IconSearch from "../../../assets/search.svg?react";
import {formLabelId} from "./FormLabel";
import classes from "./form-elements.module.css";

/** Limit for one company lookup, in milliseconds. */
const SEARCH_LIMIT_MS = 15_000;

/** Rendered inside a `FormLabel` whose `htmlFor` is `inputProps.id`. */
interface DocumentGeneratorInputProps {
    inputProps: {
        id: string;
        onChange: (e: any) => void;
        onKeyDown: (e: any) => void;
        value: string;
    };
    /** Editor-written help text for the variable; empty when there is none. */
    comment: string;
    companySearchEnabled: boolean;
    setDropdownOpen: (val: boolean) => void;
    suggestionList: string[];
    searching?: boolean;
    isDraft: boolean;
    onPromptNewFilling?: () => void;
}

export const DocumentGeneratorInput = ({
    inputProps,
    comment,
    companySearchEnabled,
    setDropdownOpen,
    suggestionList,
    searching,
    isDraft,
    onPromptNewFilling,
}: DocumentGeneratorInputProps) => {
    const {t} = useTranslation();
    const isDateInput = inputProps.id?.startsWith("Dato");

    // The company register gives no answer to some queries. The limit stops the
    // spinner, so that the user can try again.
    const [isExpired, setIsExpired] = useState(false);
    useEffect(() => {
        if (!searching) return;
        setIsExpired(false);
        const timer = setTimeout(() => setIsExpired(true), SEARCH_LIMIT_MS);
        return () => clearTimeout(timer);
    }, [searching]);

    const isSearching = Boolean(searching) && !isExpired;

    // Editor-written help text. Rendered after the input; `.docgenField` is
    // column-reverse, so it sits above the typed line, and CSS opens it only
    // while the input has focus, as the old app does. It is always in the
    // DOM so that `aria-describedby` reaches it. Everything here lives inside
    // the field's <label> (the bordered box), so a name taken from the label
    // would read the help text, the typed value and the reset button as part
    // of the name. `aria-labelledby` narrows the name to the label text alone.
    const labelId = formLabelId(inputProps.id);
    const descriptionId = comment ? `${inputProps.id}-description` : undefined;
    const description = comment && (
        <p id={descriptionId} className={classes.docgenComment}>
            {comment}
        </p>
    );

    // Tells the user that a completed search found no company. The limit gives
    // no result, thus it keeps the message away.
    const [hasNoHits, setHasNoHits] = useState(false);
    const didSearch = useRef(false);
    useEffect(() => {
        if (isSearching) {
            didSearch.current = true;
            setHasNoHits(false);
            return;
        }
        if (!didSearch.current) return;
        didSearch.current = false;
        if (isExpired) return;
        setHasNoHits((suggestionList?.length ?? 0) === 0);
    }, [isSearching, isExpired, suggestionList]);

    const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (isDraft && onPromptNewFilling) {
                e.target.blur();
                onPromptNewFilling();
                return;
            }
            if (companySearchEnabled) {
                setDropdownOpen(true);
            }
            if (isDateInput && suggestionList?.length > 0) {
                setDropdownOpen(true);
            }
        },
        [isDraft, onPromptNewFilling, companySearchEnabled, setDropdownOpen, isDateInput, suggestionList]
    );

    if (companySearchEnabled) {
        return (
            <div className={classes.docgenField}>
                <div className={classes.docgenRow}>
                    <IconSearch
                        width={18}
                        height={18}
                        aria-hidden
                        className={classes.docgenInputIcon}
                    />
                    <input
                        id={inputProps.id}
                        value={inputProps.value}
                        onChange={inputProps.onChange}
                        onKeyDown={inputProps.onKeyDown}
                        onFocus={handleFocus}
                        placeholder={t("Action.GetCompanyInfo")}
                        className={`${classes.docgenInput} ${classes.docgenInputWithIcon}`}
                        aria-labelledby={labelId}
                        aria-describedby={descriptionId}
                    />
                    {isSearching && <Loader size={16} />}
                </div>
                {hasNoHits && inputProps.value && (
                    <Text
                        size="xs"
                        c="dimmed"
                        role="status"
                        className={classes.docgenSearchNoHits}
                    >
                        {t("Info.NoHits")}
                    </Text>
                )}
                {description}
            </div>
        );
    }

    return (
        <div className={classes.docgenField}>
            <div className={classes.docgenRow}>
                <input
                    id={inputProps.id}
                    value={inputProps.value}
                    onChange={inputProps.onChange}
                    onKeyDown={inputProps.onKeyDown}
                    onFocus={handleFocus}
                    className={classes.docgenInput}
                    aria-labelledby={labelId}
                    aria-describedby={descriptionId}
                />
                {isSearching && <Loader size={16} />}
            </div>
            {description}
        </div>
    );
};
